import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Category, Confession } from '../types/confession';
import { getConfessions, SortOption, TimePeriod } from '../lib/api';
import { ConfessionCard } from './ConfessionCard';
import { CommentsModal } from './CommentsModal';

interface ConfessionFeedProps {
  category: Category | 'all';
  sort?: SortOption;
  period?: TimePeriod;
  refreshTrigger?: number;
  excludeIds?: string[];
}

export function ConfessionFeed({ category, sort = 'new', period = 'day', refreshTrigger, excludeIds = [] }: ConfessionFeedProps) {
  const [confessions, setConfessions] = useState<Confession[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Single modal state - lifted to feed level to prevent flicker
  const [modalConfession, setModalConfession] = useState<{ id: string; commentCount: number } | null>(null);
  
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadConfessions = useCallback(async (reset: boolean = false) => {
    if (isLoading || (!hasMore && !reset)) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getConfessions({
        category,
        cursor: reset ? undefined : cursor,
        limit: 20,
        sort,
        period: (sort === 'hot' || sort === 'top') ? period : undefined,
      });

      // Store all confessions — filtering happens at render time to avoid race conditions
      if (reset) {
        setConfessions(response.confessions);
      } else {
        setConfessions(prev => [...prev, ...response.confessions]);
      }
      
      setCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load confessions');
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [category, sort, period, cursor, hasMore, isLoading]);

  // Reset when category, sort, or period changes
  useEffect(() => {
    setConfessions([]);
    setCursor(undefined);
    setHasMore(true);
    setIsInitialLoad(true);
    loadConfessions(true);
  }, [category, sort, period, refreshTrigger]);

  // Setup infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadConfessions();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, loadConfessions]);

  const handleConfessionUpdate = useCallback((updated: Confession) => {
    setConfessions(prev =>
      prev.map(c => (c.id === updated.id ? updated : c))
    );
  }, []);

  // Open comments modal for a specific confession
  const handleOpenComments = useCallback((confessionId: string, commentCount: number) => {
    console.log('[ConfessionFeed] handleOpenComments called:', confessionId, commentCount);
    setModalConfession({ id: confessionId, commentCount });
  }, []);

  const handleCloseComments = useCallback(() => {
    setModalConfession(null);
  }, []);

  // Filter out excluded IDs at render time (fixes DailyTop race condition)
  const displayedConfessions = useMemo(
    () => confessions.filter(c => !excludeIds.includes(c.id)),
    [confessions, excludeIds]
  );

  // Loading skeleton
  if (isInitialLoad) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border border-white/10 p-4 animate-pulse">
            <div className="flex items-center gap-2 mb-3">
              <div className="skeleton w-20 h-6 rounded-full" />
            </div>
            <div className="space-y-2 mb-4">
              <div className="skeleton h-4 rounded w-full" />
              <div className="skeleton h-4 rounded w-3/4" />
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="skeleton w-12 h-8 rounded-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && confessions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-4xl mb-4">😵</p>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => loadConfessions(true)}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty state
  if (displayedConfessions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-4xl mb-4">🤫</p>
        <p className="text-gray-400 mb-2">No confessions yet in this category</p>
        <p className="text-gray-500 text-sm">Be the first to confess!</p>
      </div>
    );
  }

  return (
    <div>
      {displayedConfessions.map((confession) => (
        <ConfessionCard
          key={confession.id}
          confession={confession}
          onUpdate={handleConfessionUpdate}
          onOpenComments={handleOpenComments}
        />
      ))}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="h-20 flex items-center justify-center">
        {isLoading && (
          <div className="flex items-center gap-2 text-gray-400">
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            <span>Loading more...</span>
          </div>
        )}
        {!hasMore && displayedConfessions.length > 0 && (
          <p className="text-gray-500 text-sm">That's all the confessions! 🤖</p>
        )}
      </div>

      {/* Single comments modal instance */}
      <CommentsModal
        confessionId={modalConfession?.id || ''}
        totalCount={modalConfession?.commentCount || 0}
        isOpen={modalConfession !== null}
        onClose={handleCloseComments}
      />
    </div>
  );
}
